import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getMe, MeResponse } from '../../api/session';
import { RESPONSE_CODES } from '../../consts';
import type { RootState } from '../store';

type SessionStatus = 'idle' | 'loading' | 'authenticated' | 'unauthenticated' | 'failed';

interface SessionState {
  status: SessionStatus;
  data: MeResponse | null;
}

const initialState: SessionState = { status: 'idle', data: null };

export const fetchMe = createAsyncThunk('session/fetchMe', () => getMe(), {
  // Prevent duplicate calls: skip if a fetch is already running or completed.
  condition: (_, { getState }) => (getState() as RootState).session.status === 'idle',
});

const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMe.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchMe.fulfilled, (state, action) => {
        state.status = 'authenticated';
        state.data = action.payload;
      })
      .addCase(fetchMe.rejected, (state, action) => {
        // Only treat the session as expired (→ login redirect) for 401 errors.
        // 500: apiFetch already redirects the browser to /error.
        // Network errors (TypeError: Failed to fetch) and other failures: use
        // 'failed' so AuthBoundary can surface an error state instead of
        // leaving the user stuck on "Loading…" indefinitely.
        if (action.error.message === RESPONSE_CODES.UNAUTHORIZED) {
          state.status = 'unauthenticated';
        } else {
          state.status = 'failed';
        }
        state.data = null;
      });
  },
});

export default sessionSlice.reducer;
